import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import DashboardApp from './DashboardApp.jsx';
import CheckoutApp from './CheckoutApp.jsx';

/**
 * DP-2: "Two hostnames: one for the dashboard, one for the hosted checkout,
 * served from the same build. Keeping them separate means a cookie or a policy
 * relaxation on one cannot affect the other."
 *
 * One bundle is produced and the root decides which application to mount. In
 * production Nginx serves this same file on both hostnames; the separation that
 * matters — cookie scope, CSP, session surface — is enforced by the host.
 *
 * Locally that separation buys nothing and costs a second dev server, so both
 * run on one port and the *path* decides. `/c/:id` and `/l/:code` are checkout
 * routes and can never be anything else, which is what makes this safe: the two
 * route sets do not overlap, so sharing an origin cannot mount the wrong app.
 */
function isCheckoutHost() {
  const { hostname, port, pathname } = window.location;

  /**
   * Path first, always.
   *
   * On a shared origin this is the only signal that can distinguish them. On
   * split hostnames it is simply redundant — a checkout path is never served on
   * the dashboard host.
   */
  if (/^\/(c|l)\//.test(pathname)) return true;

  const checkoutUrl = import.meta.env.VITE_CHECKOUT_URL;
  const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL;

  /**
   * If the two are configured to the same origin, there is nothing left to
   * match on and the path above has already answered. Falling through to the
   * host check here would mount the checkout over the whole dashboard.
   */
  if (checkoutUrl && dashboardUrl) {
    try {
      if (new URL(checkoutUrl).origin === new URL(dashboardUrl).origin) return false;
    } catch {
      /* a malformed env value must not take the app down */
    }
  }

  if (checkoutUrl) {
    try {
      const configured = new URL(checkoutUrl);
      if (configured.hostname === hostname && (!configured.port || configured.port === port)) return true;
    } catch {
      /* as above */
    }
  }

  if (/^(checkout|pay)\./i.test(hostname)) return true;

  // A second dev server on 5174, for anyone testing the split-host setup.
  return port === '5174';
}

const App = isCheckoutHost() ? CheckoutApp : DashboardApp;

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
